import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { setActiveTenantId } from '../services/api';

const SWITCH_COOLDOWN_MS = 800;

interface Tenant {
  id: number;
  name: string;
  domain: string;
}

interface TenantContextValue {
  selectedTenantId: number | null;
  selectedTenantName: string | null;
  tenants: Tenant[];
  setTenants: (tenants: Tenant[]) => void;
  switchTenant: (tenantId: number | null) => void;
  isSwitching: boolean;
  isAdmin: boolean;
  setIsAdmin: (val: boolean) => void;
}

const TenantContext = createContext<TenantContextValue>({
  selectedTenantId: null,
  selectedTenantName: null,
  tenants: [],
  setTenants: () => {},
  switchTenant: () => {},
  isSwitching: false,
  isAdmin: false,
  setIsAdmin: () => {},
});

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const switchTenant = useCallback((tenantId: number | null) => {
    if (isSwitching) return;
    const ownTid = parseInt(localStorage.getItem('own_tid') || '', 10) || null;
    const effectiveId = tenantId ?? ownTid;
    setActiveTenantId(effectiveId);
    setSelectedTenantId(tenantId);
    if (effectiveId !== null) {
      localStorage.setItem('tid', String(effectiveId));
    }
    setIsSwitching(true);
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    cooldownTimer.current = setTimeout(() => setIsSwitching(false), SWITCH_COOLDOWN_MS);
  }, [isSwitching]);

  const selectedTenantName = tenants.find(t => t.id === selectedTenantId)?.name ?? null;

  return (
    <TenantContext.Provider value={{ selectedTenantId, selectedTenantName, tenants, setTenants, switchTenant, isSwitching, isAdmin, setIsAdmin }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
